import LossProfit from 'assets/icons/LossProfit.svg'
import IncomeIcon from 'assets/icons/pemasukan-2.svg'
import ExpenseIcon from 'assets/icons/pengeluaran-2.svg'
import ProfitIcon from 'assets/icons/ProfitIcon.svg'
import { useMemo, useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Card, CardBody, Col, Collapse, Row, Spinner } from 'reactstrap'
import styled from 'styled-components'
import { newFormatThousandSeparator } from 'utils/formatNumber'

const ResponsiveTitle = styled.h6`
  font-weight: normal;
  margin-bottom: 0.25rem;
  color: #343a40;
  font-size: 1rem;
  @media (max-width: 992px) {
    font-size: 0.9rem;
  }
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`

const ResponsiveAmount = styled.h5`
  font-weight: bold;
  margin: 0;
  font-size: 1.25rem;
  @media (max-width: 992px) {
    font-size: 1.15rem;
  }
  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`

const ResponsiveIcon = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #f6fef9;
  padding: 8px;
  @media (max-width: 992px) {
    width: 52px;
    height: 52px;
  }
  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`

const formatRupiah = (value) => `Rp${newFormatThousandSeparator(value)}`

const buildBreakdown = (items, type) => {
  const totals = {}

  items?.forEach((item) => {
    if (item.incomeorexpense !== type) return
    const category = item.label || 'Lainnya'
    totals[category] = (totals[category] || 0) + item.amount
  })

  return Object.entries(totals)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
}

const ExpandableFlowCard = ({
  icon,
  title,
  amount,
  amountClass,
  backgroundColor,
  isFetching,
  items,
  total,
  isExpanded,
  onToggle,
}) => {
  const hasItems = items.length > 0

  return (
    <Card
      className="p-3 border-0 shadow-sm"
      style={{ backgroundColor, minHeight: '102px' }}
      data-testid="cashflow-profit-card"
    >
      <CardBody className="p-0">
        <div
          className="d-flex align-items-center gap-2 gap-md-3"
          style={{ cursor: hasItems ? 'pointer' : 'default' }}
          onClick={hasItems ? onToggle : undefined}
        >
          <ResponsiveIcon src={icon} alt={title} />
          <div className="flex-grow-1">
            <ResponsiveTitle>{title}</ResponsiveTitle>
            {isFetching ? (
              <Spinner size="sm" color="primary" />
            ) : (
              <ResponsiveAmount className={amountClass}>{amount}</ResponsiveAmount>
            )}
          </div>
          {hasItems && (
            <div className="flex-shrink-0" style={{ color: '#98A2B3' }}>
              {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          )}
        </div>

        <Collapse isOpen={isExpanded}>
          <div className="mt-3 border-top pt-2">
            {items.map(({ name, value }) => {
              const pct =
                total > 0 ? Math.round((value / total) * 100) : 0
              return (
                <div
                  key={name}
                  className="d-flex justify-content-between align-items-center py-1"
                  style={{ fontSize: '13px' }}
                >
                  <span className="text-truncate">{name}</span>
                  <span className="text-nowrap ms-2">
                    {formatRupiah(value)}
                    <small className="text-muted"> ({pct}%)</small>
                  </span>
                </div>
              )
            })}
          </div>
        </Collapse>
      </CardBody>
    </Card>
  )
}

const ProfitLossCardSection = ({
  res,
  totalExpense,
  totalIncome,
  totalProfit,
  items,
}) => {
  const [expanded, setExpanded] = useState({ income: false, expense: false })

  const breakdown = useMemo(
    () => ({
      income: buildBreakdown(items, 'income'),
      expense: buildBreakdown(items, 'expense'),
    }),
    [items],
  )

  const toggle = (type) =>
    setExpanded((prev) => ({ ...prev, [type]: !prev[type] }))

  return (
    <Row className="gy-4 gx-md-4 align-items-start">
      <Col xs="12" md="4">
        <ExpandableFlowCard
          icon={IncomeIcon}
          title="Pemasukan"
          amount={`Rp${newFormatThousandSeparator(totalIncome)}`}
          amountClass="text-success"
          backgroundColor="#F6FEF9"
          isFetching={res?.isFetching}
          items={breakdown.income}
          total={breakdown.income.reduce(
            (sum, item) => sum + item.value,
            0,
          )}
          isExpanded={expanded.income}
          onToggle={() => toggle('income')}
        />
      </Col>

      <Col xs="12" md="4">
        <ExpandableFlowCard
          icon={ExpenseIcon}
          title="Pengeluaran"
          amount={`Rp${newFormatThousandSeparator(totalExpense)}`}
          amountClass="text-danger"
          backgroundColor="#FFFBFA"
          isFetching={res?.isFetching}
          items={breakdown.expense}
          total={breakdown.expense.reduce(
            (sum, item) => sum + item.value,
            0,
          )}
          isExpanded={expanded.expense}
          onToggle={() => toggle('expense')}
        />
      </Col>

      <Col xs="12" md="4">
        <Card
          className="p-3 border-0 shadow-sm"
          style={{
            backgroundColor: totalProfit >= 0 ? '#F3FEFF' : '#FFFCF5',
            minHeight: '102px',
          }}
          data-testid="cashflow-profit-card"
        >
          <CardBody className="d-flex align-items-center gap-2 gap-md-3 p-0">
            <ResponsiveIcon
              src={totalProfit >= 0 ? ProfitIcon : LossProfit}
              alt={totalProfit >= 0 ? 'Keuntungan' : 'Kerugian'}
            />
            <div>
              <ResponsiveTitle>
                {totalProfit >= 0 ? 'Keuntungan' : 'Kerugian'}
              </ResponsiveTitle>
              {res?.isFetching ? (
                <Spinner size="sm" color="primary" />
              ) : (
                <ResponsiveAmount
                  className={totalProfit >= 0 ? 'text-primary' : 'text-danger'}
                >
                  {totalProfit >= 0 ? '+ ' : '- '}
                  Rp{newFormatThousandSeparator(Math.abs(totalProfit))}
                </ResponsiveAmount>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

export default ProfitLossCardSection
